const BASE_INVENTORY_UNITS = ['UN', 'KG', 'LT']

const VOLUME_FACTORS = {
  ML: 1,
  LT: 1000
}

const WEIGHT_FACTORS = {
  GR: 1,
  KG: 1000
}

const normalizeUnit = unit => String(unit || '').trim().toUpperCase()

export const getCanonicalBaseUnit = unit => {
  const normalizedUnit = normalizeUnit(unit)

  if (normalizedUnit === 'U' || normalizedUnit === 'UND' || normalizedUnit === 'UN') return 'UN'
  if (normalizedUnit === 'KG') return 'KG'
  if (normalizedUnit === 'LT' || normalizedUnit === 'L') return 'LT'

  return normalizedUnit
}

export const isCanonicalBaseUnit = unit => BASE_INVENTORY_UNITS.includes(getCanonicalBaseUnit(unit))

const getUnitScale = unit => {
  const normalizedUnit = getCanonicalBaseUnit(unit)

  if (VOLUME_FACTORS[normalizedUnit]) {
    return { type: 'volume', factor: VOLUME_FACTORS[normalizedUnit] }
  }

  if (WEIGHT_FACTORS[normalizedUnit]) {
    return { type: 'weight', factor: WEIGHT_FACTORS[normalizedUnit] }
  }

  return null
}

const formatNumericValue = value => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return ''

  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  }).format(numericValue)
}

export const getAllowedInputUnits = baseUnit => {
  const canonicalBaseUnit = getCanonicalBaseUnit(baseUnit)

  if (canonicalBaseUnit === 'KG') return ['KG', 'GR']
  if (canonicalBaseUnit === 'LT') return ['LT', 'ML']
  if (canonicalBaseUnit === 'UN') return ['UN']

  return canonicalBaseUnit ? [canonicalBaseUnit] : []
}

export const getInputUnitOptions = baseUnit =>
  getAllowedInputUnits(baseUnit).map(value => ({
    value,
    label: value
  }))

export const getBaseUnitLabel = unit => {
  const canonicalBaseUnit = getCanonicalBaseUnit(unit)
  return canonicalBaseUnit ? `Unidad base: ${canonicalBaseUnit}` : 'Unidad base sin definir'
}

export const getDefaultInputUnit = baseUnit => {
  const canonicalBaseUnit = getCanonicalBaseUnit(baseUnit)
  return canonicalBaseUnit || ''
}

export const convertInventoryQuantity = (value, fromUnit, toUnit) => {
  if (value === '' || value === null || value === undefined) return ''

  const numericValue = Number(String(value).replace(',', '.'))
  if (!Number.isFinite(numericValue)) return ''

  const fromScale = getUnitScale(fromUnit)
  const toScale = getUnitScale(toUnit)

  if (!fromScale || !toScale || fromScale.type !== toScale.type) {
    return numericValue
  }

  return (numericValue * fromScale.factor) / toScale.factor
}

export const formatInventoryQuantity = (value, unit) => {
  if (value === '' || value === null || value === undefined) return '-'

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return String(value)

  const canonicalBaseUnit = getCanonicalBaseUnit(unit)
  const formattedValue = formatNumericValue(numericValue)

  return canonicalBaseUnit ? `${formattedValue} ${canonicalBaseUnit}` : formattedValue
}

export const formatInputQuantity = (value, inputUnit) => {
  if (value === '' || value === null || value === undefined) return '-'

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return String(value)

  const normalizedInputUnit = normalizeUnit(inputUnit)
  const formattedValue = formatNumericValue(numericValue)

  return normalizedInputUnit ? `${formattedValue} ${normalizedInputUnit}` : formattedValue
}

export const getRecipeQuantityInputValue = component => {
  if (
    component.displayQuantity !== '' &&
    component.displayQuantity !== null &&
    component.displayQuantity !== undefined
  ) {
    return component.displayQuantity
  }

  return component.qtyRequired ?? ''
}

export const formatRecipeQuantityFromBase = (baseValue, baseUnit, preferredDisplayUnit) => {
  const normalizedBaseUnit = getCanonicalBaseUnit(baseUnit)
  const normalizedPreferredUnit = normalizeUnit(preferredDisplayUnit)
  const numericValue = Number(baseValue)

  if (!Number.isFinite(numericValue)) return '-'

  let displayUnit = normalizedPreferredUnit

  if (!displayUnit) {
    if (normalizedBaseUnit === 'LT' && numericValue < 1) {
      displayUnit = 'ML'
    } else if (normalizedBaseUnit === 'KG' && numericValue < 1) {
      displayUnit = 'GR'
    } else {
      displayUnit = normalizedBaseUnit
    }
  }

  const displayValue = convertInventoryQuantity(numericValue, normalizedBaseUnit, displayUnit)
  const formattedValue = formatNumericValue(displayValue)

  return displayUnit ? `${formattedValue} ${displayUnit}` : formattedValue
}

export const getRecipeQuantityStoredHint = (inputValue, inputUnit, baseUnit) => {
  const normalizedBaseUnit = getCanonicalBaseUnit(baseUnit)
  const normalizedInputUnit = normalizeUnit(inputUnit)

  if (!normalizedBaseUnit || !normalizedInputUnit) {
    return ''
  }

  if (normalizedBaseUnit === normalizedInputUnit) {
    return `Se guardará en ${normalizedBaseUnit} porque coincide con la unidad base del item.`
  }

  const normalizedValue = convertInventoryQuantity(inputValue, normalizedInputUnit, normalizedBaseUnit)
  const numericValue = Number(normalizedValue)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return ''

  return `El backend la normalizará a ${formatNumericValue(numericValue)} ${normalizedBaseUnit}.`
}

export const getRecipeQuantityUnitOptions = getInputUnitOptions
export const getDefaultRecipeDisplayUnit = getDefaultInputUnit
export const convertRecipeQuantity = convertInventoryQuantity
